import React, { ReactElement, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { ReactState } from '../../Types/Types'
import Button from '../Button'
import Checkbox from '../Checkbox'
import {
    RoundedButton,
    RoundedButtonProps
} from '../../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'

/**
 * Component properties type.
 */
export type InlinePromptProps = {
    message: string | ReactElement
    onConfirm?: (messageChecked?: boolean) => void
    confirmButtonText: string
    onCancel?: () => void
    cancelButtonText: string
    defaultChecked?: boolean
    checkMessage?: string
    infoMode?: boolean
    isRouterMode?: boolean
}

/**
 * Inline configurable generic prompt component.
 *
 * @param message Message to be displayed.
 * @param messageRenderElement Element to be rendered instead of message, if provided.
 * @param onConfirm Callback on confirm.
 * @param confirmButtonText Confirmation button text.
 * @param onCancel Callback on cancel.
 * @param cancelButtonText Cancel button text.
 */
const InlinePrompt: React.FC<InlinePromptProps> = ({
    message,
    onConfirm,
    confirmButtonText,
    onCancel,
    cancelButtonText,
    defaultChecked = false,
    checkMessage,
    infoMode = false,
    isRouterMode
}: InlinePromptProps): ReactElement => {
    const [messageChecked, setMessageChecked]: ReactState<boolean> = useState<boolean>(defaultChecked)

    const renderButton = (buttonProps: RoundedButtonProps): ReactElement =>
        isRouterMode ? <RoundedButton {...buttonProps} /> : <Button {...buttonProps} />

    useHotkeys('enter', (): void => onConfirm?.(messageChecked))
    useHotkeys('esc', (e: Event): void => {
        e?.preventDefault()
        onCancel?.()
    })

    return (
        <div className='inline-prompt'>
            <div className='message'>{message}</div>
            {checkMessage ? (
                <div className='check-message'>
                    <Checkbox isChecked={messageChecked} onChange={setMessageChecked} labelRight={checkMessage} />
                </div>
            ) : (
                <></>
            )}
            <div className='actions'>
                {onCancel ? (
                    renderButton({
                        onClick: onCancel,
                        children: cancelButtonText,
                        isOutlined: true
                    })
                ) : (
                    <></>
                )}
                {!infoMode ? (
                    renderButton({
                        onClick: (): void => onConfirm && onConfirm(messageChecked),
                        children: confirmButtonText,
                        isOutlined: false
                    })
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}

export default InlinePrompt
